import React, { useEffect, useState, useMemo } from "react";
import { Menu } from "../../components/menu";
import CateringOrderSummary from "../../components/cateringPackages/CateringOrderSummary";
import PackageDetailFilterBar from "../../components/cateringPackages/PackageDetailFilterBar";
import { useDispatch, useSelector } from "react-redux";
import {
  getRestaurantItemsCategoriesAsync,
  getCateringPackageDetailAsync,
} from "../../redux/features/restaurants/restaurantThunks";
import { addCateringCartPackage, addItemToCateringPackage, clearCateringItemsToRestore } from "../../redux/features/restaurants/restaurantSlice";

import {
  selectBranchDetails,
  selectCartItems,
  selectShowProductDetail,
  selectCateringItems,
  selectCateringCategories,
  selectCateringPackages,
  selectCateringRestaurantId,
  selectCartRestaurantDetails,
  selectCateringCartFilters,
  selectCateringCartPackages,
  selectCateringItemsToRestore,
  selectIsModifyOrderMode,
  selectModifyOrderPax,
} from "../../redux/features/restaurants/restaurantSelectors";

import Product from "../../components/menu/product";
import { useParams, useNavigate } from "react-router-dom";

const PlaceCateringOrder = () => {
  console.log("PlaceCateringOrder component rendered!");
  const { packageId } = useParams(); // Get package ID from URL
  console.log("Package ID from URL:", packageId);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const branchDetails = useSelector(selectBranchDetails);
  const showProductDetail = useSelector(selectShowProductDetail);
  const cateringItems = useSelector(selectCateringItems);
  const cateringCategories = useSelector(selectCateringCategories);
  const cateringPackages = useSelector(selectCateringPackages);
  const cateringRestaurantId = useSelector(selectCateringRestaurantId);
  const cartRestaurantDetails = useSelector(selectCartRestaurantDetails);
  const filters = useSelector(selectCateringCartFilters);
  const cateringCartPackages = useSelector(selectCateringCartPackages);
  const itemsToRestore = useSelector(selectCateringItemsToRestore);
  
  // Check if this is a modify order mode from Redux
  const isModifyOrderMode = useSelector(selectIsModifyOrderMode);
  const modifyOrderPax = useSelector(selectModifyOrderPax);

  const [loading, setLoading] = useState(true);
  const [packageDetail, setPackageDetail] = useState(null);
  
  console.log('📦 Package Detail Page - Modify Order Mode (Redux):', {
    'isModifyOrderMode': isModifyOrderMode,
    'modifyOrderPax': modifyOrderPax,
    'Will pass to Menu': isModifyOrderMode
  });

  // Find the selected package from existing catering packages data
  const selectedPackage = cateringPackages?.find(pkg => pkg.id === parseInt(packageId));
  console.log(selectedPackage,"selectedPackages");
  
  // Get current restaurant ID from selected package first, then fall back to cateringRestaurantId or cartRestaurantDetails
  // This ensures we use the correct restaurant for the package, especially when coming from past orders
  const restaurantId = selectedPackage?.restaurant_id || cateringRestaurantId || cartRestaurantDetails?.restaurantId || 1;
  console.log("Using restaurant ID:", restaurantId, "from selectedPackage:", selectedPackage?.restaurant_id, "cateringRestaurantId:", cateringRestaurantId);

  // Create complete package data by merging selectedPackage with packageDetail
  const completePackageData = useMemo(() => {
    if (!selectedPackage) return null;
    if (!packageDetail) return selectedPackage;
    
    return {
      ...selectedPackage,
      ...packageDetail, // This includes complete unavailability_schedule and other detailed info
      id: selectedPackage.id // Ensure ID stays from selectedPackage
    };
  }, [selectedPackage, packageDetail]);

  useEffect(() => {
    const fetchCateringData = async () => {
      if (!packageId) {
        console.error("No package ID provided");
        navigate('/catering-packages');
        return;
      }

      // Modify order mode flags are managed in Redux, no need to clear session storage

      try {
        setLoading(true);
        console.log("Fetching catering package detail for:", { restaurantId, packageId });

        // Check if we need to fetch data for a different restaurant
        const isRestaurantDifferent = cateringRestaurantId !== restaurantId;
        const promises = [];

        // Always fetch restaurant categories to ensure we have the latest data
        // This ensures we have up-to-date categories including any new items like salads & appetizers
        if (isRestaurantDifferent) {
          console.log("Restaurant changed, refetching categories...");
        } else {
          console.log("Fetching latest restaurant categories...");
        }
        promises.push(
          dispatch(getRestaurantItemsCategoriesAsync(restaurantId, "en", packageId))
        );

        // Always fetch package detail for the specific package to get fresh data
        console.log("Fetching fresh catering package detail on mount for package:", packageId);
        promises.push(
          dispatch(getCateringPackageDetailAsync(restaurantId, packageId, "", "en", 3))
            .then(response => {
              console.log("Fresh package detail received:", response);
              setPackageDetail(response);
            })
        );

        // Wait for all API calls to complete
        if (promises.length > 0) {
          await Promise.all(promises);
        }

        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch catering package data:", error);
        setLoading(false);
      }
    };

    fetchCateringData();
  }, [dispatch, packageId, restaurantId, cateringRestaurantId, selectedPackage]);

  // Log package data when it changes
  useEffect(() => {
    if (packageDetail) {
      console.log("Package detail loaded:", packageDetail);
    }
  }, [packageDetail]);

  // Log selected package data
  useEffect(() => {
    if (selectedPackage) {
      console.log("Selected package:", selectedPackage);
    }
  }, [selectedPackage]);

  // Auto-add package to cart with complete details when package detail is loaded
  useEffect(() => {
    if (selectedPackage && packageDetail) {
      const currentPackageId = parseInt(packageId);
      const isAlreadyInCart = cateringCartPackages.some(pkg => pkg.id === currentPackageId);
      
      if (!isAlreadyInCart) {
        // Merge selectedPackage with packageDetail to get complete package info
        const completePackage = {
          ...selectedPackage,
          ...packageDetail, // This includes pax_limit and other detailed info
          id: selectedPackage.id // Ensure ID stays from selectedPackage
        };
        
        console.log("Auto-adding complete package to cart:", completePackage);
        dispatch(addCateringCartPackage(completePackage));
      }
    }
  }, [selectedPackage, packageDetail, packageId, cateringCartPackages, dispatch]);

  useEffect(() => {
    if (cateringCategories) {
      console.log("Categories loaded:", cateringCategories);
      console.log("Categories count:", cateringCategories.length);
      console.log("Category names:", cateringCategories.map(cat => ({
        id: cat.id,
        name_en: cat.name_en,
        name_ar: cat.name_ar,
        status: cat.status,
        items_count: cat.items_count
      })));
    }
  }, [cateringCategories]);

  useEffect(() => {
    if (cateringItems && itemsToRestore && Array.isArray(itemsToRestore) && itemsToRestore.length > 0) {
      console.log("🔄 [Item Restoration] Starting restoration from Redux");
      console.log("🔄 Items to restore:", itemsToRestore);
      console.log("🔄 Current package ID:", packageId);
      
      try {
        // Find the package in cart (it should already be there from navigation)
        const currentPackageId = parseInt(packageId);
        const existingPackage = cateringCartPackages.find(pkg => pkg.id === currentPackageId);
        
        if (existingPackage) {
          console.log("🔄 Found package in cart:", existingPackage.name_en);
          
          // Group items by category to properly reconstruct addOrderHistory
          const itemsByCategory = {};
          itemsToRestore.forEach(itemToRestore => {
            const categoryId = itemToRestore.category_id;
            if (!itemsByCategory[categoryId]) {
              itemsByCategory[categoryId] = [];
            }
            itemsByCategory[categoryId].push(itemToRestore);
          });
          
          // Get the current PAX to calculate item prices
          const currentPax = filters?.pax || 20;
          
          // Process each category in order
          Object.keys(itemsByCategory).sort((a, b) => parseInt(a) - parseInt(b)).forEach(categoryId => {
            const categoryItems = itemsByCategory[categoryId];
            
            // Find the category limit to determine how many free items are allowed
            const categoryLimit = existingPackage.categories?.find(cat => cat.id === parseInt(categoryId));
            let maxFreeItems = 0;
            
            if (categoryLimit && categoryLimit.choose_rule && categoryLimit.choose_rule.length > 0) {
              const sortedRules = [...categoryLimit.choose_rule].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
              const matchingRule = sortedRules.find(rule => currentPax >= parseInt(rule.pax));
              
              if (matchingRule && matchingRule.choose > 0) {
                maxFreeItems = matchingRule.choose;
              }
            }
            
            console.log(`🔄 Category ${categoryId}: Max free items = ${maxFreeItems}`);
            
            // Calculate total instances in category
            const totalCategoryInstances = categoryItems.reduce((sum, item) => sum + item.quantity, 0);
            const totalChargeableInCategory = Math.max(0, totalCategoryInstances - maxFreeItems);
            
            console.log(`🔄 Total instances in category: ${totalCategoryInstances}`);
            console.log(`🔄 Total chargeable in category: ${totalChargeableInCategory}`);
            
            // Build a flat list of all item instances with their metadata
            // Key insight: Free instances come first (up to maxFreeItems), then chargeable ones
            // Within free/chargeable groups, items appear in API order
            
            // First pass: collect metadata about each item
            const itemMetadata = [];
            categoryItems.forEach((itemToRestore) => {
              const fullItem = cateringItems.find(item => item.id === itemToRestore.menu_id);
              
              if (fullItem) {
                // Calculate unit price
                const unitPrice = fullItem.add_on_options && Array.isArray(fullItem.add_on_options) && fullItem.add_on_options.length > 0
                  ? (() => {
                      const matchingOption = fullItem.add_on_options.find(option => {
                        const paxFrom = parseInt(option.pax_from || 0);
                        const paxTo = parseInt(option.pax_to || 999);
                        return currentPax >= paxFrom && currentPax <= paxTo;
                      });
                      return matchingOption && matchingOption.price ? parseFloat(matchingOption.price) : 0;
                    })()
                  : 0;
                
                // Determine how many of this item's instances are chargeable
                const addOnPrice = itemToRestore.add_on_price || 0;
                let chargeableForThisItem = 0;
                if (unitPrice > 0 && addOnPrice > 0) {
                  chargeableForThisItem = Math.round(addOnPrice / unitPrice);
                }
                
                const freeForThisItem = itemToRestore.quantity - chargeableForThisItem;
                
                console.log(`🔄 Item ${fullItem.name_en}: ${itemToRestore.quantity} total (${freeForThisItem} free, ${chargeableForThisItem} chargeable)`);
                
                itemMetadata.push({
                  itemId: itemToRestore.menu_id,
                  itemName: fullItem.name_en,
                  totalQuantity: itemToRestore.quantity,
                  freeInstances: freeForThisItem,
                  chargeableInstances: chargeableForThisItem,
                  unitPrice: unitPrice
                });
              }
            });
            
            // Second pass: build allInstances array with proper ordering
            // All free instances come first (in item order), then all chargeable instances (in item order)
            const allInstances = [];
            let globalInstanceCounter = 0;
            
            // Add all free instances first
            itemMetadata.forEach(meta => {
              for (let i = 0; i < meta.freeInstances; i++) {
                allInstances.push({
                  itemId: meta.itemId,
                  itemName: meta.itemName,
                  isChargeable: false,
                  globalOrder: globalInstanceCounter++,
                  unitPrice: meta.unitPrice
                });
              }
            });
            
            // Then add all chargeable instances
            itemMetadata.forEach(meta => {
              for (let i = 0; i < meta.chargeableInstances; i++) {
                allInstances.push({
                  itemId: meta.itemId,
                  itemName: meta.itemName,
                  isChargeable: true,
                  globalOrder: globalInstanceCounter++,
                  unitPrice: meta.unitPrice
                });
              }
            });
            
            console.log(`🔄 All instances in category:`, allInstances);
            
            // Now rebuild items with correct addOrderHistory based on when their instances appear
            categoryItems.forEach((itemToRestore) => {
              const fullItem = cateringItems.find(item => item.id === itemToRestore.menu_id);
              
              if (fullItem) {
                // Find all instances of this item in the global order
                const itemInstances = allInstances.filter(inst => inst.itemId === itemToRestore.menu_id);
                const addOrderHistory = itemInstances.map(inst => inst.globalOrder);
                
                console.log(`🔄 Restoring ${fullItem.name_en}:`);
                console.log(`🔄   Quantity: ${itemToRestore.quantity}`);
                console.log(`🔄   addOrderHistory:`, addOrderHistory);
                console.log(`🔄   Chargeable instances:`, itemInstances.filter(i => i.isChargeable).map(i => i.globalOrder));
                
                // Create the item object with reconstructed addOrderHistory
                const itemToAdd = {
                  ...fullItem,
                  quantity: itemToRestore.quantity,
                  category_id: itemToRestore.category_id || fullItem.category_id,
                  addOrderHistory: addOrderHistory
                };
                
                // Add the item with the correct addOrderHistory
                dispatch(addItemToCateringPackage({
                  packageId: currentPackageId,
                  item: itemToAdd,
                  packageMeta: {
                    title: existingPackage.name_en || existingPackage.title,
                    basePrice: existingPackage.basePrice || 0
                  }
                }));
              } else {
                console.warn(`🔄 ⚠️ Could not find full item data for menu_id: ${itemToRestore.menu_id}`);
              }
            });
          });
          
          console.log("🔄 ✅ Item restoration complete, clearing itemsToRestore from Redux");
          // Clear the items to restore from Redux after processing
          dispatch(clearCateringItemsToRestore());
        } else {
          console.warn('🔄 ⚠️ Package not found in cart for restoration');
        }
      } catch (error) {
        console.error('🔄 ❌ Error restoring items from past order:', error);
        // Clear the Redux state even on error
        dispatch(clearCateringItemsToRestore());
      }
    }
  }, [cateringItems, itemsToRestore, packageId, cateringCartPackages, dispatch, filters]);

  // Helper function to format price with KWD currency prefix
  const formatPrice = (price) => {
    if (typeof price === "string" && price.startsWith("KWD ")) {
      return price;
    }
    const numericPrice = parseFloat(price);
    return `KWD ${numericPrice.toFixed(3)}`;
  };

  // Helper function to calculate package price based on pax
  const calculatePackagePrice = (packageData, pax) => {
    if (!packageData || !pax || pax <= 0) return 0;

    // Check if package has new pricing_tiers structure
    if (packageData.pricing_tiers && Array.isArray(packageData.pricing_tiers)) {
      // Sort tiers by PAX in descending order to find the highest applicable tier
      const sortedTiers = [...packageData.pricing_tiers].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
      
      // Find the appropriate pricing tier for current PAX
      const matchingTier = sortedTiers.find(tier => 
        pax >= parseInt(tier.pax)
      );

      if (matchingTier && matchingTier.price) {
        return parseFloat(matchingTier.price);
      }

      // If no exact match, use the lowest tier (fallback to first tier)
      if (packageData.pricing_tiers.length > 0) {
        return parseFloat(packageData.pricing_tiers[0].price);
      }
    }

    // Fallback to old structure for backward compatibility
    const paxRanges = [
      {
        from: packageData.pax_range_from || 0,
        to: packageData.pax_range_to || 0,
        price: packageData.pax_price || "KWD 0.000"
      },
      {
        from: packageData.pax_range_from2 || 0,
        to: packageData.pax_range_to2 || 0,
        price: packageData.pax_price2 || "KWD 0.000"
      },
      {
        from: packageData.pax_range_from3 || 0,
        to: packageData.pax_range_to3 || 0,
        price: packageData.pax_price3 || "KWD 0.000"
      },
      {
        from: packageData.pax_range_from4 || 0,
        to: packageData.pax_range_to4 || 0,
        price: packageData.pax_price4 || "KWD 0.000"
      },
      {
        from: packageData.pax_range_from5 || 0,
        to: packageData.pax_range_to5 || 0,
        price: packageData.pax_price5 || "KWD 0.000"
      }
    ];

    // Find the matching pax range
    for (const range of paxRanges) {
      if (range.from > 0 && range.to > 0 && pax >= range.from && pax <= range.to) {
        // Extract numeric value from price string
        const priceMatch = range.price.match(/[\d.]+/);
        return priceMatch ? parseFloat(priceMatch[0]) : 0;
      }
    }

    // If no range matches, return 0 or default price
    return 0;
  };

  // Memoize props to prevent infinite re-renders
  const memoizedSelectedPackages = useMemo(() => {
    if (!completePackageData) return [];
    
    // Calculate the package price based on current pax and complete package data
    const calculatedPrice = calculatePackagePrice(completePackageData, filters.pax);
    
    // Get items from Redux catering cart packages instead of packageDetail
    const currentPackageId = parseInt(packageId);
    const cartPackage = cateringCartPackages.find(pkg => pkg.id === currentPackageId);
    const cartItems = cartPackage?.items || [];
    
    // Process items from cart with proper pricing
    const processedItems = cartItems.map(item => ({
      ...item,
      price: parseFloat(item.price?.replace(/[^0-9.]/g, '') || 0) // Extract numeric price
    }));
    
    // Use complete package data that already includes all detailed info
    const finalPackage = {
      ...completePackageData,
      title: completePackageData.name_en || completePackageData.title || 'Package', // Ensure title is available
      basePrice: calculatedPrice,
      items: processedItems, // Use cart items, not API items
    };
    
    const result = [finalPackage];
    
    console.log("Memoized selected packages:", result);
    return result;
  }, [completePackageData, filters.pax, cateringCartPackages, packageId]);


  return (
    <>
      {loading ? (
        <div className="flex justify-center items-center h-screen">
          <div className="spinner-border animate-spin border-t-4 border-b-4 border-gray-600 w-16 h-16 rounded-full"></div>
        </div>
      ) : (
        <div className="h-screen bg-white flex flex-col overflow-hidden">
          <PackageDetailFilterBar 
            selectedPackage={completePackageData} 
            isModifyOrderMode={isModifyOrderMode}
            modifyOrderPax={modifyOrderPax}
          />
          <div className="flex flex-1 flex-row overflow-hidden">
            <div className="w-3/4 flex flex-col flex-1 relative overflow-hidden">
              <Menu 
                branchDetails={branchDetails} 
                isCatering={true} 
                activePackageId={parseInt(packageId)}  
                displayName={completePackageData?.name_en || completePackageData?.title || 'Package'}
                disabled={!filters.time}
                packageData={completePackageData}
                isModifyOrderMode={isModifyOrderMode}
              />
              {!filters.time && (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50">
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-gray-600 mb-2">Please select a time slot</h3>
                    <p className="text-gray-500">Choose a time slot above to start adding items to your order</p>
                  </div>
                </div>
              )}
              {showProductDetail && <Product />}
            </div>
            <div className="w-1/4 border-l border-gray-200 bg-[#FBFBFB] flex-shrink-0 flex flex-col overflow-hidden">
              {!showProductDetail && (
                <CateringOrderSummary 
                  selectedPackages={memoizedSelectedPackages} 
                  showPackageDetailButtons={true}
                  disabled={!filters.time}
                  isModifyOrderMode={isModifyOrderMode}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PlaceCateringOrder; 